/**
 * <color-rush-gate>
 *
 * Members-only gate rendered by snippets/jump-color-rush-gate.liquid while the
 * server keeps the store locked. Nothing here can open the store by itself.
 *
 * The typed email is hashed in the browser (it never leaves it) and
 * sections/jump-color-rush-check.liquid answers whether that hash is on the
 * member list in one quick request. The field also takes the shared password
 * from the members' email, hashed and checked the same way. Writing the hash
 * to the cart is what unlocks the store on the server, so the gate gets ahead
 * of it: it checks the entry as soon as one is typed and writes a member's
 * hash to the cart before Unlock is even pressed.
 *
 * Unlocking doesn't celebrate here. The padlock opens and the store is loaded
 * at once: a page being left freezes on its last frame until the next one
 * paints, so any party on this page would stall. The store plays it instead
 * (assets/jump-color-rush-celebrate.js).
 *
 * A member's hash is remembered in localStorage. A new cart (after checkout,
 * say) comes back locked, and the gate checks the saved hash again on its own
 * so the member doesn't type their email twice.
 *
 * States, mirrored on [data-state] for the CSS: idle, checking, denied, error,
 * unlocked, open (the countdown ran out while the gate was on screen).
 */
const COLOR_RUSH_STORAGE_KEY = 'colorRushMember';

/* Long enough to see the padlock open before the page starts to change. */
const COLOR_RUSH_UNLOCK_BEAT = 450;

/* How long typing has to pause before the email is checked ahead of Unlock. */
const COLOR_RUSH_PRECHECK_DELAY = 300;

/* The unlocked store is asked for under its own URL so a prefetch of it can be reused. */
const COLOR_RUSH_UNLOCKED_PARAM = 'color_rush';

/*
 * A fetch no app has wrapped. Apps patch window.fetch to watch the cart, and on
 * this store one of them (Notify Me!'s SDK) turns a 0.5s cart write into 2.5s.
 * A blank iframe hands out a clean copy of the browser's own fetch.
 */
function colorRushNativeFetch() {
  if (!colorRushNativeFetch.cached) {
    try {
      const frame = document.createElement('iframe');
      frame.hidden = true;
      frame.setAttribute('aria-hidden', 'true');
      document.body.appendChild(frame);
      colorRushNativeFetch.cached = frame.contentWindow.fetch.bind(window);
    } catch (error) {
      colorRushNativeFetch.cached = window.fetch.bind(window);
    }
  }
  return colorRushNativeFetch.cached;
}

/* The browser's own rule for type="email", since the field also takes the password and is plain text. */
const COLOR_RUSH_EMAIL =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/*
 * The first 16 hex characters of SHA-256(lowercased email or password): the
 * member list's format. The server hashes the shared password the same way.
 */
async function colorRushHash(email) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()));
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

class ColorRushGate extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.color-rush__panel');
    this.form = this.querySelector('.color-rush__form');
    this.input = this.querySelector('.color-rush__input');
    this.message = this.querySelector('[data-message]');
    this.countdown = this.querySelector('[data-countdown]');
    this.units = Object.fromEntries([...this.querySelectorAll('[data-unit]')].map((el) => [el.dataset.unit, el]));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.endAt = Number(this.dataset.end) || NaN;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    this.input.addEventListener('input', () => {
      if (this.dataset.state === 'denied' || this.dataset.state === 'error') this.setState('idle');
      clearTimeout(this.precheckTimer);
      this.precheckTimer = setTimeout(() => this.precheck(), COLOR_RUSH_PRECHECK_DELAY);
    });

    /* Answers and cart writes by hash, so a precheck and the real submit share one request each. */
    this.answers = new Map();
    this.cartWrites = new Map();

    if (typeof trapFocus === 'function') trapFocus(this.panel, this.input);
    if (this.countdown) this.tick();

    /*
     * Back on the gate from the unlocked URL means the last unlock didn't take
     * on the server. Asking again by itself could loop forever, so wait for
     * the member to press Unlock.
     */
    const retried = new URL(window.location.href).searchParams.has(COLOR_RUSH_UNLOCKED_PARAM);
    const saved = this.readSaved();
    if (retried) this.setState('error', this.dataset.msgError);
    else if (saved) this.restore(saved);
    else this.precheck();
  }

  disconnectedCallback() {
    clearTimeout(this.tickTimer);
  }

  readSaved() {
    try {
      return localStorage.getItem(COLOR_RUSH_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  setState(state, text = '') {
    this.dataset.state = state;
    this.message.textContent = text;
    this.form.setAttribute('aria-busy', state === 'checking');
    this.input.disabled = state === 'checking' || state === 'unlocked';
  }

  /**
   * What's in the field: an email (anything with an @, which must be a valid
   * address) or the password (anything else without spaces). Null if neither.
   */
  entry() {
    const value = this.input.value.trim();
    if (!value) return null;
    if (value.includes('@')) return COLOR_RUSH_EMAIL.test(value) ? { value, isPassword: false } : null;
    return /\s/.test(value) ? null : { value, isPassword: true };
  }

  /**
   * Checks whatever valid entry is in the field before Unlock is pressed
   * (typing, autofill, a logged-in customer's prefill). For a member it starts
   * the slow cart write right away, so it is usually done by the time they
   * click.
   */
  async precheck() {
    const entry = this.entry();
    if (!entry) return;
    const hash = await colorRushHash(entry.value);
    if (await this.lookup(hash).catch(() => false)) {
      this.writeCart(hash).catch((error) => console.error('[Color Rush]', error));
    }
  }

  lookup(hash) {
    if (!this.answers.has(hash)) {
      const answer = colorRushNativeFetch()(`${this.dataset.checkUrl}&q=${hash}`, { cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error(`Member check failed: ${response.status}`);
          return response.text();
        })
        .then((html) => html.includes('data-color-rush-result="member"'));
      answer.catch(() => this.answers.delete(hash));
      this.answers.set(hash, answer);
    }
    return this.answers.get(hash);
  }

  /* Rejects when the cart refuses the write (a busy store answers 429), so the gate never navigates in vain. */
  writeCart(hash) {
    if (!this.cartWrites.has(hash)) {
      const write = colorRushNativeFetch()(this.dataset.cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _color_rush_member: hash } }),
      }).then((response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      });
      write.catch(() => this.cartWrites.delete(hash));
      this.cartWrites.set(hash, write);
    }
    return this.cartWrites.get(hash);
  }

  async submit() {
    if (this.dataset.state === 'checking' || this.dataset.state === 'unlocked') return;

    const entry = this.entry();
    if (!entry) {
      this.setState('error', this.dataset.msgInvalid);
      this.restartAnimation('is-shaking');
      return;
    }
    this.check(await colorRushHash(entry.value), entry.isPassword);
  }

  async check(hash, isPassword = false) {
    if (this.dataset.state === 'checking' || this.dataset.state === 'unlocked') return;
    this.setState('checking');

    try {
      if (await this.lookup(hash)) return this.unlock(hash);

      this.setState('denied', isPassword ? this.dataset.msgWrongPassword : this.dataset.msgNotMember);
      this.restartAnimation('is-shaking');
    } catch (error) {
      console.error('[Color Rush]', error);
      this.setState('error', this.dataset.msgError);
      this.restartAnimation('is-shaking');
    }
  }

  /**
   * A remembered member back with a fresh cart (after checkout, say). The head
   * hid the panel before first paint (html.cr-restoring), so the member never
   * sees the gate: check the hash and write it to the cart at the same time,
   * then go straight back to the store, no padlock, no celebration. The gate
   * only shows if that doesn't work out; if the list changed, the hash is
   * forgotten and the plain gate shows, not an error.
   */
  async restore(hash) {
    this.setState('checking');
    const written = this.writeCart(hash);
    written.catch(() => {});

    try {
      if (await this.lookup(hash)) {
        await written;
        window.location.replace(this.unlockedUrl());
        return;
      }
      this.forget();
    } catch (error) {
      console.error('[Color Rush]', error);
    }
    this.setState('idle');
    document.documentElement.classList.remove('cr-restoring');
  }

  /* The store under its own URL, so a prefetch of it can be reused. */
  unlockedUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set(COLOR_RUSH_UNLOCKED_PARAM, 'unlocked');
    url.hash = '';
    return url.href;
  }

  /**
   * The padlock opens and the store loads at once, as soon as the cart write
   * that unlocks it has landed (usually already, thanks to the precheck).
   * Browsers with speculation rules prefetch it as soon as the write is in.
   */
  unlock(hash) {
    try {
      localStorage.setItem(COLOR_RUSH_STORAGE_KEY, hash);
      sessionStorage.setItem('colorRushReveal', '1');
    } catch (error) {
      // Private mode: this visit still unlocks, the next visit asks again.
    }

    this.setState('unlocked', this.dataset.msgOpening);
    clearTimeout(this.tickTimer);
    if (typeof removeTrapFocus === 'function') removeTrapFocus();

    const storeUrl = this.unlockedUrl();
    const cartWritten = this.writeCart(hash).then(() => this.prefetch(storeUrl));
    const beat = new Promise((resolve) => setTimeout(resolve, this.reducedMotion ? 0 : COLOR_RUSH_UNLOCK_BEAT));
    Promise.all([cartWritten, beat])
      .then(() => window.location.replace(storeUrl))
      .catch((error) => {
        console.error('[Color Rush]', error);
        this.setState('error', this.dataset.msgError);
        this.restartAnimation('is-shaking');
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.input);
        if (this.countdown) this.tick();
      });
  }

  prefetch(url) {
    if (!HTMLScriptElement.supports || !HTMLScriptElement.supports('speculationrules')) return;
    const rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [url] }] });
    document.head.appendChild(rules);
  }

  forget() {
    try {
      localStorage.removeItem(COLOR_RUSH_STORAGE_KEY);
    } catch (error) {}
  }

  /* Re-adding a class doesn't replay its animation; a reflow in between does. */
  restartAnimation(className, element = this.panel) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
  }

  /**
   * Counts down to the end of the lock, 24 hours after the start. At zero the
   * gate says the doors are opening and asks the server again straight away;
   * if the store is somehow still locked, it reloads once a minute until it
   * isn't.
   */
  tick() {
    if (Number.isNaN(this.endAt)) {
      this.countdown.hidden = true;
      return;
    }

    const remaining = Math.max(this.endAt - Date.now(), 0);
    const seconds = Math.floor(remaining / 1000);
    this.setUnit('hours', Math.floor(seconds / 3600));
    this.setUnit('minutes', Math.floor((seconds % 3600) / 60));
    this.setUnit('seconds', seconds % 60);

    if (remaining === 0) {
      if (this.dataset.state === 'idle') this.setState('open', this.dataset.msgOpen);

      /*
       * The server opens by itself at the end: ask past any cached copy of
       * this gate, once. ?_cr= carries when it was asked for, so a gate fetched
       * after the end that is still locked means this clock is off: don't loop.
       */
      const url = new URL(window.location.href);
      const askedAt = parseInt(url.searchParams.get('_cr'), 36) || 0;
      if (askedAt < this.endAt) {
        url.searchParams.set('_cr', Date.now().toString(36));
        this.tickTimer = setTimeout(() => window.location.replace(url.href), 1500);
        return;
      }
      this.tickTimer = setTimeout(() => window.location.reload(), 60000);
      return;
    }

    this.tickTimer = setTimeout(() => this.tick(), 1000 - (Date.now() % 1000) + 20);
  }

  setUnit(name, value) {
    const element = this.units[name];
    const text = String(value).padStart(2, '0');
    if (!element || element.textContent === text) return;

    const isFirstPaint = element.textContent === '--';
    element.textContent = text;
    if (!isFirstPaint && !this.reducedMotion) this.restartAnimation('is-ticking', element.parentElement);
  }
}

if (!customElements.get('color-rush-gate')) customElements.define('color-rush-gate', ColorRushGate);
